import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type ReactNode } from "react";
import { useAdminDeviceInstallationDetail } from "@/hooks/use-admin-device-installations";
import { useAdminOrgDetail } from "@/hooks/use-admin-orgs";
import { formatOrgDay } from "@/lib/format-org-day";

export const Route = createFileRoute("/_admin/device-installations/$id")({
  component: AdminDeviceInstallationDetailPage,
});

function AdminDeviceInstallationDetailPage() {
  const { t } = useTranslation(["installations", "admin", "vocabulary"]);
  const { id } = Route.useParams();
  const { data, isLoading, isError, error } = useAdminDeviceInstallationDetail(id);
  // The owning org's zone dates Added the way that org's staff see it.
  const { data: org } = useAdminOrgDetail(data?.org_id ?? "");

  if (isLoading) {
    return <p className="p-4 text-sm text-slate-500">…</p>;
  }
  if (isError) {
    return (
      <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
        {(error as Error)?.message ?? t("installations:empty.title")}
      </div>
    );
  }
  if (!data) return null;

  // Testing is paused out of service, or while a saved date stands in for the
  // live one (an in-service device at an inactive or archived facility) — the
  // web record card's reading of the same two members.
  const savedDue = data.paused_next_test_due_date ?? null;
  const testingPaused = data.device_status === "out_of_service" || savedDue !== null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">{data.id.slice(0, 8)}…</h1>

      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-base font-semibold text-slate-700">{t("installations:title")}</h2>
        <dl className="grid grid-cols-2 gap-4">
          <Field label={t("admin:deviceInstallation.columns.orgId")}>
            <code className="text-xs text-slate-500">{data.org_id}</code>
          </Field>
          <Field label={t("installations:columns.facility")}>
            <code className="text-xs text-slate-500">{data.facility_id}</code>
          </Field>
          <Field label={t("installations:columns.serialNumber")}>{data.serial_number ?? "—"}</Field>
          <Field label={t("installations:columns.manufacturer")}>
            {data.manufacturer_label ?? "—"}
          </Field>
          <Field label={t("installations:columns.model")}>{data.model_label ?? "—"}</Field>
          <Field label={t("installations:columns.size")}>
            {data.size_inches === null || data.size_inches === undefined
              ? "—"
              : data.size_inches.toFixed(2)}
          </Field>
          <Field label={t("installations:columns.category")}>
            {t(`vocabulary:device_category.${data.device_category}`)}
          </Field>
          <Field label={t("installations:columns.status")}>
            {t(`vocabulary:device_status.${data.device_status}`)}
          </Field>
          <Field label={t("installations:columns.createdAt")}>
            {formatOrgDay(data.created_at, org?.timezone)}
          </Field>
          <Field label={t("installations:columns.nextTestDueDate")}>
            {testingPaused
              ? savedDue
                ? t("installations:record.testingPaused", { date: savedDue })
                : t("installations:record.testingPausedNoDate")
              : (data.next_test_due_date ?? "—")}
          </Field>
          <Field label={t("admin:deviceInstallation.assetLink")}>
            {data.inventory_summary && data.device_inventory_id ? (
              <Link
                to="/temporary-devices/$id"
                params={{ id: data.device_inventory_id }}
                className="text-primary hover:underline"
              >
                <code className="text-xs">{data.device_inventory_id.slice(0, 8)}…</code>
              </Link>
            ) : (
              "—"
            )}
          </Field>
        </dl>
      </section>

      <Link
        to="/device-installations"
        search={{ org_id: undefined }}
        className="text-sm text-slate-500 hover:text-slate-700"
      >
        ← {t("installations:title")}
      </Link>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm text-slate-700">{children}</dd>
    </div>
  );
}
